import React, { useEffect, useState, useRef } from 'react';
import s from './RecentProjects.module.css';
import { useAppDispatch } from '../../../../store/store';
import {
    selectRecentProjects,
    selectIsLoadingProject,
    selectHasNewProject,
} from '../../../../store/selectors/projectSelectors';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUser } from '../../../../store/selectors/authSelectors';
import Spinner from '../../../../shared/ui/spinner/Spinner';
import {
    deleteProject,
    dupicateProject,
    getRecentProjects,
} from '../../../../shared/api/textProjectApi';
import { CurrentProject } from '../../../../shared/types/projectTypes';
import { RenameModal } from './ProjectRenameModal';
import { toast } from 'react-toastify';
import DropDownMenu from '../../../../shared/ui/dropdown-menu/DropDownMenu';
import IconCalendar from '../../../../shared/ui/icons/IconCalendar';
import AvatarComponent from '../../../../shared/ui/avatar/AvatarComponent';
import { getShortTextFromHtml } from '../../../../shared/helpers/transformData';
import { socialMediaOptions } from '../../../../shared/constant/constant';

const timeOptions = [
    'last 7 days',
    'last 30 days',
    'last 90 days',
    'last 180 days',
    'last 365 days',
];

const RecentProjects = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const projects = useSelector(selectRecentProjects);
    const user = useSelector(selectUser);
    const isLoading = useSelector(selectIsLoadingProject);
    const hasNewProject = useSelector(selectHasNewProject);
    const [selectedSocialMedia, setSelectedSocialMedia] = useState('all');
    const [selectedTime, setSelectedTime] = useState('Last 7 days');
    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [activeTab, setActiveTab] = useState('all');

    const socialMediaOptionsWithAllOption = [
        {
            value: 'all',
            disabled: false,
        },
        ...socialMediaOptions,
    ];

    const filteredProjects = projects.filter((project) => {
        if (selectedSocialMedia !== 'all' && project.content_type !== selectedSocialMedia) {
            return false;
        }

        const projectDate = new Date(project.created_at);
        const now = new Date();
        let cutoffDate: Date;

        switch (selectedTime) {
            case 'last 7 days':
                cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'last 30 days':
                cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'last 90 days':
                cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                break;
            case 'last 180 days':
                cutoffDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
                break;
            case 'last 365 days':
                cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                break;
            default:
                cutoffDate = new Date(0);
        }

        if (projectDate < cutoffDate) {
            return false;
        }

        switch (activeTab) {
            case 'published':
                return project.is_published === true;
            case 'scheduled':
                return project.schedule.timestamp != '' && !project.is_published;
            case 'all':
            default:
                return true;
        }
    });

    const handleEdit = (projectId: string) => {
        setMenuOpenId(null);
        navigate(`/text_project/${projectId}`);
    };

    const handleDelete = async (projectId: string) => {
        setMenuOpenId(null);
        try {
            await dispatch(deleteProject({ projectId }));
            toast.success('Project deleted successfully');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to delete project');
        }
    };

    const handleDuplicate = async (projectId: string) => {
        setMenuOpenId(null);
        try {
            await dispatch(dupicateProject({ projectId }));
            toast.success('Project duplicated successfully');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to duplicate project');
        }
        if (user?.id) {
            dispatch(getRecentProjects(user.id));
        }
    };

    useEffect(() => {
        if (!menuOpenId) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpenId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpenId]);

    const handleProjectClick = (projectId: string) => {
        navigate(`/custom-post/${projectId}`);
    };

    const [renameProject, setRenameProject] = useState<CurrentProject | null>(null);
    const [renameModal, setRenameModal] = useState<boolean>(false);
    const handleRename = (project: CurrentProject) => {
        setMenuOpenId(null);
        setRenameModal(true);
        setRenameProject(project);
    };

    return (
        <>
            <div className={s.container}>
                <div className={s.header}>
                    <p className={s.title}>Recent Projects</p>
                    <div className={s.header_actions}>
                        <DropDownMenu
                            width={130}
                            options={socialMediaOptionsWithAllOption}
                            selectedOption={selectedSocialMedia}
                            setSelectedOption={(option) => setSelectedSocialMedia(option)}
                        />
                        <DropDownMenu
                            width={160}
                            options={timeOptions.map((time) => ({
                                value: time,
                                disabled: false,
                            }))}
                            selectedOption={selectedTime}
                            setSelectedOption={(option) => setSelectedTime(option)}
                            icon={<IconCalendar />}
                        />
                    </div>
                </div>
                <div className={s.tab}>
                    <div
                        className={`${s.tab_item} ${activeTab === 'all' ? s.active : ''}`}
                        onClick={() => setActiveTab('all')}
                    >
                        All posts({projects.length})
                    </div>
                    <div
                        className={`${s.tab_item} ${activeTab === 'published' ? s.active : ''}`}
                        onClick={() => setActiveTab('published')}
                    >
                        Published({projects.filter((p) => p.is_published === true).length})
                    </div>
                    <div
                        className={`${s.tab_item} ${activeTab === 'scheduled' ? s.active : ''}`}
                        onClick={() => setActiveTab('scheduled')}
                    >
                        Scheduled(
                        {
                            projects.filter((p) => p.schedule.timestamp != '' && !p.is_published)
                                .length
                        }
                        )
                    </div>
                </div>

                {isLoading ? (
                    <Spinner />
                ) : (
                    <div className={s.projects}>
                        {filteredProjects.length === 0 && (
                            <p className={s.no_projects}>No recent projects</p>
                        )}
                        {filteredProjects.map((project) => (
                            <div
                                key={project.id}
                                className={s.project_container}
                                onClick={() => handleProjectClick(project.id)}
                                style={{ cursor: 'pointer', position: 'relative' }}
                            >
                                <div className={s.project}>
                                    {project.image?.url ? (
                                        <div className={s.image_container}>
                                            {activeTab === 'all' &&
                                                project.schedule.timestamp !== '' &&
                                                !project.is_published && (
                                                    <div className={s.scheduled_label}>
                                                        SCHEDULED
                                                    </div>
                                                )}
                                            <img
                                                src={project.image.url}
                                                alt={project.name}
                                                className={s.project_image}
                                                onContextMenu={(e) => e.preventDefault()}
                                                draggable={false}
                                            />
                                        </div>
                                    ) : (
                                        <div className={s.empty_image}>No Image</div>
                                    )}

                                    <div className={s.user_info}>
                                        <AvatarComponent
                                            avatarUrl={user?.profile_picture ?? ''}
                                            userName={
                                                user?.name || user?.username || user?.email || ''
                                            }
                                            width={20}
                                            height={20}
                                        />
                                        <p className={s.user_name}>
                                            {user?.name || user?.username || user?.email || ''}
                                        </p>
                                    </div>
                                    <p className={s.project_name}>
                                        {getShortTextFromHtml(project.title || '')}
                                    </p>
                                </div>
                                {/* <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                                >
                                    <p className={s.project_created_at}>
                                        Created: {formatDate(project.created_at)}
                                    </p>
                                    <div
                                        style={{ cursor: 'pointer', padding: '0 4px' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpenId(
                                                menuOpenId === project.id ? null : project.id,
                                            );
                                        }}
                                    >
                                        <span style={{ fontSize: 20, userSelect: 'none' }}>⋮</span>
                                    </div>
                                    {menuOpenId === project.id && (
                                        <div
                                            ref={menuRef}
                                            style={{
                                                position: 'absolute',
                                                right: 10,
                                                top: 60,
                                                background: '#fff',
                                                border: '1px solid  rgba(0, 0, 0, 0.12)',
                                                borderRadius: 8,

                                                zIndex: 100,
                                                minWidth: 120,
                                            }}
                                        >
                                            <div
                                                className={s.menu_item}
                                                onClick={() => handleRename(project as any)}
                                            >
                                                Rename
                                            </div>
                                            <div
                                                className={s.menu_item}
                                                onClick={() => handleEdit(project.id)}
                                            >
                                                Edit
                                            </div>
                                            <div
                                                className={s.menu_item}
                                                onClick={() => handleDuplicate(project.id)}
                                            >
                                                Duplicate
                                            </div>

                                            <div
                                                className={s.menu_item}
                                                onClick={() => handleDelete(project.id)}
                                            >
                                                Delete
                                            </div>
                                        </div>
                                    )}
                                </div> */}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <RenameModal
                isOpen={renameModal}
                onClose={() => setRenameModal(false)}
                project={renameProject}
            />
        </>
    );
};

export default RecentProjects;
